import { plainTextOf, sanitizeFormatting } from "@/components/WritingEditor";

// FORMATTING + TWS ANNOTATION COEXISTENCE — two representational layers. The STUDENT'S FORMATTING (`item.formatting`, sanitized allowed-subset
// HTML) belongs to the student's prose; a TWS ANNOTATION (the thesis / main-idea span in Essay view; MAIN POINT / WORKING HERE in Paragraph
// Focus) describes how TWS reads that prose. This module DERIVES display-only HTML that carries both: it never writes into the stored
// formatting, and it never guesses — spans come from canonical-text offsets, the text nodes of the formatted HTML are aligned MONOTONICALLY
// against the canonical plain text `plainTextOf` derives (the same validator the editors already trust), and only text-node slices that fall
// inside a span are wrapped — one fragment per text node, so no element boundary is ever crossed and bold / italic / underline / list /
// blockquote structure stays exactly as the student made it. Any doubt → null (keep the formatting, no highlight). ONE mapping for every
// annotation kind: `wrapCanonicalSpan` is the shared primitive; the thesis and Paragraph Focus layers only differ in the wrapper they make.

const INLINE = new Set(["B", "STRONG", "I", "EM", "U"]);
const isWs = (ch) => ch === " " || ch === "\n" || ch === "\t" || ch === "\r" || ch === "\u00a0";

const textNodesOf = (root) => {
  const out = [];
  const walk = (n) => { n.childNodes.forEach((c) => { if (c.nodeType === 3) out.push(c); else if (c.nodeType === 1) walk(c); }); };
  walk(root);
  return out;
};

// map every text-node character to its offset in the canonical text (or -1 when plainTextOf dropped it: trim / newline collapse)
function alignTextNodes(nodes, canonical) {
  let c = 0;
  const maps = [];
  for (const node of nodes) {
    const s = node.nodeValue, map = new Array(s.length);
    for (let i = 0; i < s.length; i++) {
      const ch = s[i] === "\u00a0" ? " " : s[i];
      while (c < canonical.length && canonical[c] !== ch && isWs(canonical[c])) c++;   // synthesized block / <br> newlines live only in the canonical text
      if (c < canonical.length && canonical[c] === ch) { map[i] = c; c++; }
      else if (isWs(ch)) map[i] = -1;                                                 // whitespace plainTextOf dropped
      else return null;                                                               // the HTML does not spell the canonical text — no highlight
    }
    maps.push(map);
  }
  for (; c < canonical.length; c++) if (!isWs(canonical[c])) return null;
  return maps;
}

const setAttrs = (el, attrs) => Object.entries(attrs).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") el.setAttribute(k, String(v)); });

// climb out of inline wrappers the fragment begins, so a label sits before the student's <b>/<i>/<u> instead of inheriting it
const labelAnchor = (node) => {
  let n = node;
  while (n.parentNode && INLINE.has(n.parentNode.tagName) && n.parentNode.firstChild === n) n = n.parentNode;
  return n;
};

// the formatting is a valid display of the canonical text (the formatter's own rule) → its sanitized root, else null
export function formattedRoot(html, canonical) {
  if (typeof document === "undefined" || !html || !canonical) return null;
  const clean = sanitizeFormatting(html);
  if (plainTextOf(clean) !== String(canonical).trim()) return null;
  const root = document.createElement("div");
  root.innerHTML = clean;
  return root;
}

// THE SHARED PRIMITIVE — wrap the canonical span [start, end) inside `root`, one wrapper per text node, `makeWrapper(k)` supplying each element.
// Returns the wrappers in document order, or null (root untouched) when no SAFE mapping exists. Re-aligns from the live DOM every time, so
// several non-overlapping spans can be applied to the same root in sequence.
export function wrapCanonicalSpan(root, canonical, span, makeWrapper) {
  if (!root || !span || !canonical) return null;
  const text = String(canonical);
  const { start, end } = span;
  if (!(Number.isInteger(start) && Number.isInteger(end) && start >= 0 && end > start && end <= text.length)) return null;
  if (!text.slice(start, end).trim()) return null;
  const nodes = textNodesOf(root);
  const maps = alignTextNodes(nodes, text);
  if (!maps) return null;
  const pieces = [];                                                                  // (node, from, to) slices that fall inside the span
  nodes.forEach((node, k) => {
    const map = maps[k];
    let from = -1, to = -1;
    for (let i = 0; i < map.length; i++) if (map[i] >= start && map[i] < end) { if (from < 0) from = i; to = i + 1; }
    if (from >= 0) pieces.push({ node, from, to });
  });
  if (!pieces.length) return null;
  let covered = "";
  pieces.forEach(({ node, from, to }) => { covered += node.nodeValue.slice(from, to); });
  if (covered.replace(/\s|\u00a0/g, "") !== text.slice(start, end).replace(/\s/g, "")) return null;   // block boundaries are synthesized whitespace
  return pieces.map(({ node, from, to }, k) => {
    let mid = node;
    if (from > 0) mid = mid.splitText(from);
    if (to - from < mid.nodeValue.length) mid.splitText(to - from);
    const wrap = makeWrapper(k);
    mid.parentNode.insertBefore(wrap, mid);
    wrap.appendChild(mid);
    return wrap;
  });
}

// THESIS LAYER (Essay view). html: the student's formatting; canonical: the item's plain text the offsets refer to; span: { start, end }.
// Returns the annotated HTML string, or null when no SAFE inline highlight exists.
export function annotateFormatting(html, canonical, span, opts = {}) {
  const root = formattedRoot(html, canonical);
  if (!root) return null;
  const { pending = false, label = "", labelId = "", owner = "student", status = "", title = "" } = opts;
  const frags = wrapCanonicalSpan(root, canonical, span, (k) => {
    const wrap = document.createElement("span");
    wrap.className = "tws-thesis-mark";
    setAttrs(wrap, {
      "data-tws-thesis": "mark", "data-fragment": k, "data-owner": pending ? undefined : owner, "data-pending": pending ? "true" : undefined,
      "data-testid": k === 0 ? (pending ? "essay-thesis-candidate" : "essay-thesis-mark") : "essay-thesis-mark-cont",
    });
    if (k === 0 && !pending) setAttrs(wrap, { role: "button", tabindex: "0", title, "aria-describedby": labelId || undefined });
    return wrap;
  });
  if (!frags) return null;
  if (!pending && label) {                                                            // the quiet label: ordinary accessible text, not copied as prose
    const lab = document.createElement("span");
    lab.className = "tws-thesis-label";
    lab.textContent = label;
    setAttrs(lab, { id: labelId || undefined, "data-testid": "essay-thesis-label-inline", "data-owner": owner, "data-thesis-status": status || undefined });
    const anchor = labelAnchor(frags[0]);
    anchor.parentNode.insertBefore(lab, anchor);
  }
  return root.innerHTML;
}

// INSTRUCTIONAL MARKS LAYER (Paragraph Focus mirror / context paragraphs). marks: non-overlapping [{ start, end, kind }] in canonical offsets;
// makeWrapper(mark, k) supplies each fragment element. Returns { html, failed: [kinds with no safe mapping] }, or null when the HTML is not a
// valid display of the canonical text at all (caller keeps its plain path). A failed mark leaves the formatting untouched — never a guess.
export function annotateMarks(html, canonical, marks, makeWrapper) {
  const root = formattedRoot(html, canonical);
  if (!root) return null;
  const failed = [];
  (marks || []).forEach((mk) => { if (!wrapCanonicalSpan(root, canonical, mk, (k) => makeWrapper(mk, k))) failed.push(mk.kind); });
  return { html: root.innerHTML, failed };
}
